import { useAppSelector } from 'store/store';
import { ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN } from 'shared/permissions/roles';

/**
 * A conditional wrapper component that renders its children only if the authenticated user
 * possesses Tenant Administrator or Super Administrator privileges.
 *
 * @example
 * ```tsx
 * <IsTenantAdministrator>
 *   <Button>Delete Equipment</Button>
 * </IsTenantAdministrator>
 * ```
 *
 * @param {Object} props - The component properties.
 * @param {React.ReactNode} props.children - The elements to be conditionally displayed.
 * @returns {React.ReactNode | null} The children elements if authorized, otherwise null.
 */
export function IsTenantAdministrator({ children }) {
  const groups = useAppSelector((state) => state.auth.groups);
  if (
    (groups && groups.includes(ROLE_TENANT_ADMIN)) ||
    (groups && groups.includes(ROLE_SUPER_ADMIN))
  ) {
    return children;
  }
  return null;
}

/**
 * A custom React hook to programmatically determine if the currently
 * authenticated user has Tenant Administrator or Super Administrator administrative rights.
 *
 * @remarks
 * As a React Hook, this must be called unconditionally at the top level of your
 * component function, before any conditional blocks or return statements.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const isAdmin = useIsTenantAdministrator();
 *
 *   if (isAdmin) {
 *   }
 *
 *   return <div>...</div>;
 * };
 * ```
 *
 * @returns {boolean} True if the authenticated user belongs to either administrative group, false otherwise.
 */
export function useIsTenantAdministrator(): boolean {
  const groups = useAppSelector((state) => state.auth.groups);
  return !!(
    (groups && groups.includes(ROLE_TENANT_ADMIN)) ||
    (groups && groups.includes(ROLE_SUPER_ADMIN))
  );
}
